import { MapLabel } from "PosterEditor/model/poster-project";
import { MapElement, drawSelectedRect } from "../canvas-drawing";
import { coordinatesToPoints } from "../coordinate-utils";
import { rectWithPadding } from "../rect-utils";
import {
  PointCoordinate,
  Rect,
  LabelPointerSide,
  LabelSideHorizontal,
  LabelSideVertical,
  LabelDecoration,
  allowSidePointerForDecoration,
} from "PosterEditor/model/poster-types";
import {
  Point,
  allowCornerPointerForDecoration,
} from "PosterEditor/model/poster-types";
import { pointIsInsideRect, resizeRectFromCenter } from "../point-utils";
import { convertToRGBA } from "../color-utils";
import { drawDecoration } from "../label-decoration-drawing";
import { MapDrawingConfig } from "../canvas-drawing";

export const drawMapLabels = ({
  map,
  canvasElement,
  project,
  scale,
  pointerLocation,
  elementToHighlight,
  selectedElement,
}: MapDrawingConfig) => {
  const ctx = canvasElement.current.getContext("2d");
  const labelUnderCursor = drawLabels(
    project.mapLabels,
    map,
    scale,
    ctx,
    pointerLocation,
    selectedElement?.mapLabel,
    elementToHighlight
  );
  if (labelUnderCursor.labelUnderCursor) {
    const mapElement: MapElement = {
      type: "mapLabel",
      pointCoordinate: labelUnderCursor.labelUnderCursor.pointCoordinate,
      rect: labelUnderCursor.labelUnderCursor.rect,
      element: labelUnderCursor.labelUnderCursor.label,
    };
    return { elementUnderPointerLocation: mapElement };
  }
  return { elementUnderPointerLocation: undefined };
};

const drawLabels = (
  labels: MapLabel[],
  map: any,
  scale: number,
  ctx: CanvasRenderingContext2D,
  pointerLocation: Point | undefined,
  selectedLabel: MapLabel | undefined,
  elementToHighlight: MapElement | undefined
): {
  labelUnderCursor:
    | { label: MapLabel; rect: Rect; pointCoordinate: PointCoordinate }
    | undefined;
} => {
  if (!labels) return { labelUnderCursor: undefined };
  const coordinates = labels.map((l) => l.coordinate);
  const points = coordinatesToPoints(map, scale, coordinates);

  let labelUnderCursor:
    | { label: MapLabel; rect: Rect; pointCoordinate: PointCoordinate }
    | undefined = undefined;
  points.forEach((point, index) => {
    const label = labels[index]; // Direct lookup
    const labelDrawn = drawLabel(
      ctx,
      label,
      point,
      scale,
      pointerLocation,
      label.id === selectedLabel?.id,
      elementToHighlight
    );
    if (labelDrawn && labelDrawn.isUnderCursor) {
      const pointCoordinate: PointCoordinate = {
        point: point,
        coordinate: {
          lat: label.coordinate.lat,
          lng: label.coordinate.lng,
        },
      };
      labelUnderCursor = { label, rect: labelDrawn.rect, pointCoordinate };
    }
  });
  return { labelUnderCursor };
};

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
};

const emptyLabelString = "label";

const drawLabel = (
  ctx: CanvasRenderingContext2D,
  label: MapLabel,
  point: { x: number; y: number },
  scale: number,
  pointerLocation: Point | undefined,
  isSelected: boolean,
  elementToHighlight: MapElement | undefined
): { rect: Rect; isUnderCursor: boolean } | undefined => {
  if (!label || !label.style) return undefined;

  const {
    text,
    style: {
      backgroundColor,
      backgroundOpacity,
      padding: unscaledPadding = 4,
      decorationStyle: MapLabelDecorationStyle,
      font = "Arial",
      textColor = "black",
      fontSize: unscaledFontSize,
      borderRadius = 0,
      offset: unscaledOffset,
    },
  } = label;

  const labelText = text.length > 0 ? text : emptyLabelString;

  const fontSize = unscaledFontSize * scale;
  const offset = { x: unscaledOffset.x * scale, y: unscaledOffset.y * scale };
  const padding = unscaledPadding * scale;

  let isLabelDimmed = false;
  if (
    elementToHighlight?.type === "mapLabel" &&
    (elementToHighlight?.element as MapLabel).id !== label.id
  ) {
    isLabelDimmed = true;
  }
  ctx.globalAlpha = isLabelDimmed ? 0.2 : 1;

  ctx.setLineDash([]);

  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textSize = textSizeInContext(labelText, font, fontSize, ctx);
  const textWidth = textSize.width;
  const textHeight = textSize.height;

  const offsetX = offset?.x ?? 0;
  const offsetY = offset?.y ?? 0;

  const paddingHorizontal = padding * 1;
  const paddingVertical = padding * 1;

  const boxX = point.x - paddingHorizontal + offsetX;
  const boxY = point.y - paddingVertical + offsetY;

  const boxWidth = textWidth + paddingHorizontal * 2;
  const boxHeight = textHeight + paddingVertical * 2;

  let isHighlighted = false;
  if (pointerLocation) {
    let rect: Rect = { x: boxX, y: boxY, width: boxWidth, height: boxHeight };
    if (pointIsInsideRect(pointerLocation, rect)) {
      isHighlighted = true;
    }
  }

  ctx.save();

  if (backgroundColor) {
    const opacity = backgroundOpacity ?? 1;
    ctx.fillStyle = convertToRGBA(backgroundColor, opacity);
    drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, borderRadius);
    ctx.fill();
  }

  const decoration: LabelDecoration = label.style.decorationStyle.decoration;
  const side = labelSide(
    {
      x: boxX,
      y: boxY,
      width: boxWidth,
      height: boxHeight,
    },
    point,
    allowCornerPointerForDecoration(decoration),
    allowSidePointerForDecoration(decoration)
  );

  const rect = {
    x: boxX,
    y: boxY,
    width: boxWidth,
    height: boxHeight,
  };

  //Draw a line from coordinate to the box...
  if ((label.style.pointerThickness ?? 0) > 0) {
    ctx.strokeStyle = label.style.pointerColor ?? "black";
    ctx.lineWidth = (label.style.pointerThickness ?? 1) * scale;

    drawLineToLabel(
      ctx,
      point,
      side,
      rect,
      (label.style.pointerThickness ?? 0) * scale
    );
    ctx.strokeStyle = label.style.decorationStyle.color;
    ctx.lineWidth = label.style.decorationStyle.width * scale;
  }

  drawDecoration(label.style.decorationStyle, ctx, rect, side, scale);

  ctx.fillStyle = textColor;

  const textY = boxY + textHeight / 2 + paddingVertical + textHeight * 0.11; // Text seems to be slightly too high.

  ctx.fillText(labelText, point.x + offsetX, textY);

  if (isSelected) {
    const paddedRect = rectWithPadding(rect, 10);
    drawSelectedRect(paddedRect, ctx);
  }

  ctx.restore(); // Restore canvas state

  return { rect, isUnderCursor: isHighlighted };
};

const drawLineToLabel = (
  ctx: CanvasRenderingContext2D,
  rootPoint: Point,
  side: LabelPointerSide,
  labelRect: Rect,
  decorationWidth: number
) => {
  const labelPoint = labelPointFromSide(side, labelRect, decorationWidth);
  ctx.beginPath();
  ctx.moveTo(rootPoint.x, rootPoint.y);
  ctx.lineTo(labelPoint.x, labelPoint.y);
  ctx.stroke();
};

const labelPointFromSide = (
  labelSide: LabelPointerSide,
  labelRect: Rect,
  decorationWidth: number
): { x: number; y: number } => {
  let horizontal: LabelSideHorizontal;
  let vertical: LabelSideVertical;

  const padding = decorationWidth;
  const resizedRect = resizeRectFromCenter(labelRect, padding);

  if (labelSide.horizontal === "center") {
    horizontal = "center";
  } else if (labelSide.horizontal === "left") {
    horizontal = "left";
  } else {
    horizontal = "right";
  }

  if (labelSide.vertical === "center") {
    vertical = "center";
  } else if (labelSide.vertical === "top") {
    vertical = "top";
  } else {
    vertical = "bottom";
  }

  // create point from resized rect with x, y label sides
  let x = 0;
  let y = 0;
  if (horizontal === "center") {
    x = resizedRect.x + resizedRect.width / 2;
  } else if (horizontal === "left") {
    x = resizedRect.x;
  } else {
    x = resizedRect.x + resizedRect.width;
  }

  if (vertical === "center") {
    y = resizedRect.y + resizedRect.height / 2;
  } else if (vertical === "top") {
    y = resizedRect.y;
  } else {
    y = resizedRect.y + resizedRect.height;
  }
  return { x, y };
};

const drawDogLegLineToLabel = (
  ctx: CanvasRenderingContext2D,
  rootPoint: Point,
  labelOffsetFromRoot: Point,
  padding: number,
  textSize: { width: number; height: number }
) => {
  const offset = labelOffsetFromRoot;

  const allowance = 20;

  const overlapXWithLabelArea =
    (offset.x >= 0 && offset.x < allowance) ||
    (offset.x < 0 && offset.x > -textSize.width - padding - allowance);

  const overlapYWithLabelArea =
    (offset.y >= 0 && offset.y < allowance) ||
    (offset.y < 0 && offset.y > -textSize.height - padding - allowance);

  if (overlapXWithLabelArea && overlapYWithLabelArea) {
    return;
  }

  ctx.beginPath();
  ctx.moveTo(rootPoint.x, rootPoint.y);

  let x: number = 0;
  let y: number = 0;

  const dogLegDistance = 20;
  let dogLegX = 0;
  let dogLegY = 0;

  let xLeft: number = rootPoint.x + offset.x - padding + allowance + padding;
  let xRight: number =
    rootPoint.x + offset.x + textSize.width - allowance + -allowance;
  let xMiddle: number = rootPoint.x + offset.x + textSize.width / 2;

  //Calculate X.
  if (offset.x <= -textSize.width) {
    x = xRight;
  } else if (offset.x < 0 - allowance - padding) {
    x = xMiddle;
  } else {
    x = xLeft;
  }

  //Calculate Y.
  if (offset.y <= -textSize.height - padding) {
    //Bottom of label
    dogLegY = dogLegDistance;
    y = rootPoint.y + offset.y + textSize.height + padding;
  } else if (offset.y < 0 + padding) {
    //Middle of the label.
    y = rootPoint.y + offset.y + textSize.height / 2;
  } else {
    //Top of label
    dogLegY = -dogLegDistance;
    y = rootPoint.y + offset.y - padding;
  }
  //Draw to right side of label
  ctx.lineTo(x, y + dogLegY);
  ctx.lineTo(x, y);
  ctx.stroke();
};

// UTILITY

const labelSide = (
  labelRect: Rect,
  targetPoint: Point,
  allowCorners: boolean,
  allowSides: boolean
): LabelPointerSide => {
  const labelMaxX = labelRect.x + labelRect.width;
  const labelMaxY = labelRect.y + labelRect.height;
  let horizontal: LabelSideHorizontal;
  if (allowSides && labelRect.x < targetPoint.x && labelMaxX > targetPoint.x) {
    horizontal = "center";
  } else if (labelRect.x + labelRect.width / 2 < targetPoint.x) {
    horizontal = "right";
  } else {
    horizontal = "left";
  }

  let vertical: LabelSideVertical;
  if (allowSides && labelRect.y < targetPoint.y && labelMaxY > targetPoint.y) {
    vertical = "center";
  } else if (labelRect.y + labelRect.height / 2 < targetPoint.y) {
    if (!allowCorners && allowSides && horizontal !== "center") {
      vertical = "center";
    } else {
      vertical = "bottom";
    }
  } else {
    if (!allowCorners && allowSides && horizontal !== "center") {
      vertical = "center";
    } else {
      vertical = "top";
    }
  }

  return { horizontal, vertical };
};

export const labelRectInCanvas = (
  label: MapLabel,
  ctx: CanvasRenderingContext2D,
  map: any
): Rect => {
  let size = labelSizeInContext(label, ctx);
  const point = map.project([label.coordinate.lng, label.coordinate.lat]);
  const padding = label.style.padding ?? 0;
  const offset = label.style.offset ?? { x: 0, y: 0 };
  const x = point.x - padding + offset.x;
  const y = point.y - padding + offset.y;
  return { x, y, width: size.width, height: size.height };
};

export const labelSizeInContext = (
  label: MapLabel,
  ctx: CanvasRenderingContext2D
): { width: number; height: number } => {
  const fontSize = label.style.fontSize;
  const font = label.style.font;
  const padding = label.style.padding ?? 0;

  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textMetrics = ctx.measureText(label.text);
  const textWidth = textMetrics.width;

  let textHeight = fontSize * 1.2;
  if (
    textMetrics.actualBoundingBoxAscent &&
    textMetrics.actualBoundingBoxDescent
  ) {
    textHeight =
      textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent;
  }
  const labelWidth = textWidth + padding * 2;
  const labelHeight = textHeight + padding * 2;

  return { width: labelWidth, height: labelHeight };
};

//TODO: check OffscreenCanvasRenderingContext2D for map rendering performance.

export const textSizeInContext = (
  text: string,
  font: string,
  fontSize: number,
  ctx: CanvasRenderingContext2D
) => {
  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textMetrics = ctx.measureText(text);
  const textWidth = textMetrics.width;

  let textHeight = fontSize * 1.2;
  if (
    textMetrics.actualBoundingBoxAscent &&
    textMetrics.actualBoundingBoxDescent
  ) {
    textHeight =
      textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent;
  }
  const labelWidth = textWidth;
  const labelHeight = textHeight;

  return { width: labelWidth, height: labelHeight };
};
